if (!customElements.get('rbw-ambient')) {
  class RbwAmbient extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.timers = new Set();
      this.flights = new Set();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.eye = this.closest('#rbw-site-header')?.querySelector('.rbw-brand-eye');
      this.brand = this.eye?.closest('a');
      this.lastBlink = 0;
      this.sequence = 0;
      const signal = this.controller.signal;
      this.brand?.addEventListener('pointerenter', () => this.blink(), { signal });
      this.brand?.addEventListener('focusin', () => this.blink(), { signal });
      document.addEventListener('visibilitychange', () => this.restart(), { signal });
      this.motion.addEventListener('change', () => this.restart(), { signal });
      document.addEventListener('toggle', () => {
        if (this.blocked()) this.clearFlights();
        else this.later(() => { this.blink(); this.fly(); }, 900);
      }, { capture: true, signal });
      window.addEventListener('resize', () => this.clearFlights(), { signal });
      this.revealTexts();
      this.restart();
    }
    revealTexts() {
      if (this.motion.matches || !('IntersectionObserver' in window)) return;
      const texts=document.querySelectorAll('.rbw-about-page .rbw-r-chapters>section>h2,.rbw-about-page .rbw-r-chapters>section>p');
      this.revealObserver=new IntersectionObserver(entries=>{
        for(const entry of entries) if(entry.isIntersecting){
          entry.target.classList.remove('rbw-reveal-wait');
          this.revealObserver.unobserve(entry.target);
        }
      },{threshold:.08});
      for(const text of texts){
        if(text.getBoundingClientRect().top>innerHeight){text.classList.add('rbw-reveal-wait');this.revealObserver.observe(text);}
      }
    }
    later(callback, delay) {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        if (this.isConnected) callback();
      }, delay);
      this.timers.add(timer);
    }
    blocked() {
      return document.hidden || this.motion.matches || !!document.querySelector('dialog[open],.mobile-menu[open],.rbw-catalog[open],.rbw-localization[open],.rbw-search[open],.rbw-newsletter-modal:not([hidden]),[aria-modal="true"]:not([hidden])');
    }
    restart() {
      for (const timer of this.timers) clearTimeout(timer);
      this.timers.clear();
      this.clearFlights();
      this.eye?.classList.remove('is-blinking');
      this.hidden = document.hidden || this.motion.matches;
      if (this.hidden) return;
      this.later(() => this.blinkLoop(), 900);
      this.later(() => this.flightLoop(), 4200);
    }
    blink() {
      if (!this.eye || this.blocked() || Date.now() - this.lastBlink < 1300) return;
      this.lastBlink = Date.now();
      this.eye.classList.add('is-blinking');
      this.later(() => this.eye?.classList.remove('is-blinking'), 340);
    }
    blinkLoop() {
      this.blink();
      this.later(() => this.blinkLoop(), 6000 + Math.random() * 3000);
    }
    flightLoop() {
      if (!this.blocked()) this.fly();
      this.later(() => this.flightLoop(), 21000 + Math.random() * 9000);
    }
    artwork() {
      const ns = 'http://www.w3.org/2000/svg';
      const make = (name, attrs, parent) => {
        const node = document.createElementNS(ns, name);
        for (const [key,value] of Object.entries(attrs)) node.setAttribute(key, value);
        if (parent) parent.append(node);
        return node;
      };
      const id = `rbw-wing-${Date.now()}-${++this.sequence}`;
      const left = 'M285 339 C248 290 205 164 161 122 C121 83 83 99 82 130 C77 179 91 238 119 280 C141 306 162 315 183 326 C133 317 81 317 77 354 C75 408 94 456 140 483 C185 508 229 486 277 407 Z';
      const right = 'M286 339 C357 275 399 217 465 196 C534 169 600 183 584 223 C569 274 528 342 430 357 C458 385 480 436 460 490 C449 549 398 568 356 553 C302 530 285 456 286 378 Z';
      const body = 'M250 231 L265 227 L295 315 L309 294 L317 295 L300 332 L302 388 L289 444 L266 449 L277 374 L270 333 Z';
      const svg = make('svg', {viewBox:'60 0 532 568',class:'rbw-flying-svg','aria-hidden':'true',focusable:'false'});
      const defs = make('defs', {}, svg);
      for (const [name,path] of [['left',left],['right',right],['body',body]]) {
        const clip = make('clipPath', {id:`${id}-${name}`,clipPathUnits:'userSpaceOnUse'}, defs);
        make('path', {d:path}, clip);
      }
      const filter = make('filter', {id:`${id}-alpha`,x:'0%',y:'0%',width:'100%',height:'100%'},defs);
      make('feColorMatrix', {type:'matrix',values:'0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 2 2 1 1 -2.5',result:'flame-alpha'},filter);
      make('feComposite', {in:'flame-alpha',in2:'SourceAlpha',operator:'in'},filter);
      const mask = make('mask', {id:`${id}-fire`,maskUnits:'userSpaceOnUse',x:'185',y:'0',width:'300',height:'325','mask-type':'luminance'},defs);
      const imageAttrs = {href:this.dataset.sprite,x:'0',y:'0',width:'1280',height:'1280',preserveAspectRatio:'none'};
      make('image', {...imageAttrs,filter:`url(#${id}-alpha)`},mask);
      for (const path of [left,right,body]) make('path', {d:path,fill:'black',stroke:'black','stroke-width':'48','stroke-linejoin':'round','stroke-linecap':'round'},mask);
      const fire = make('g', {class:'rbw-fire-layer'},svg);
      make('image', {...imageAttrs,mask:`url(#${id}-fire)`},fire);
      const shimmer = make('g', {class:'rbw-fire-shimmer'},svg);
      make('rect', {x:'60',y:'0',width:'532',height:'568',fill:'#ffd46a',mask:`url(#${id}-fire)`},shimmer);
      for (const side of ['left','right']) {
        const wing = make('g', {class:`rbw-wing-${side}`},svg);
        make('image', {...imageAttrs,'clip-path':`url(#${id}-${side})`},wing);
      }
      make('image', {...imageAttrs,'clip-path':`url(#${id}-body)`},svg);
      return svg;
    }
    fly(reverse = Math.random() > .5, lane = Math.random() > .5 ? 1 : 0) {
      if (this.blocked() || !this.dataset.sprite || this.flights.size >= 1) return;
      const size = innerWidth <= 940 ? 21 + Math.random() * 5.3 : 27 + Math.random() * 7.7;
      const flight = document.createElement('span');
      flight.className = 'rbw-flight';
      flight.style.width = `${size}px`;
      flight.style.height = `${size * 568 / 532}px`;
      flight.style.setProperty('--rbw-flap', `${.28 + Math.random() * .12}s`);
      flight.style.setProperty('--rbw-phase', `${-Math.random()}s`);
      flight.append(this.artwork());
      this.append(flight);
      reverse = this.lastDirection === undefined ? reverse : !this.lastDirection;
      this.lastDirection = reverse;
      const startX = reverse ? innerWidth + size : -size * 2;
      const endX = reverse ? -size * 2 : innerWidth + size;
      const floor = Math.min(innerHeight - size * 2, Math.max(145, this.eye?.closest('.rbw-header')?.getBoundingClientRect().bottom + 35 || 145));
      const range = Math.max(40, innerHeight - floor - size * 2);
      const startY = floor + range * (.15 + Math.random() * .65);
      const endY = floor + range * (.15 + Math.random() * .65);
      const arch = (Math.random() > .5 ? 1 : -1) * range * (.15 + Math.random() * .2);
      const phase = Math.random() * Math.PI * 2;
      const waves = 1.2 + Math.random() * 1.3;
      const frames = Array.from({length:37}, (_,index) => {
        const progress = index / 36;
        const wander = Math.sin(progress * Math.PI * 2 * waves + phase) * Math.sin(progress * Math.PI) * Math.min(35, range * .08);
        const y = Math.max(floor, Math.min(innerHeight - size * 2, startY + (endY-startY)*progress + Math.sin(progress*Math.PI)*arch + wander));
        const x = startX + (endX-startX)*progress + Math.sin(progress*Math.PI*3+phase)*Math.sin(progress*Math.PI)*Math.min(22,innerWidth*.035);
        return {transform:`translate3d(${x}px,${y}px,0) rotate(${Math.cos(progress*Math.PI*2*waves+phase)*16}deg)`,opacity:index===0||index===36?0:.91};
      });
      const animation = flight.animate(frames, { duration: 4600 + Math.random() * 1400, easing:'linear' });
      const item = { flight, animation };
      this.flights.add(item);
      animation.finished.catch(() => {}).finally(() => { flight.remove(); this.flights.delete(item); });
    }
    clearFlights() {
      for (const {flight,animation} of this.flights) { animation.cancel(); flight.remove(); }
      this.flights.clear();
    }
    disconnectedCallback() {
      this.revealObserver?.disconnect();
      this.controller?.abort();
      for (const timer of this.timers || []) clearTimeout(timer);
      this.timers?.clear();
      this.clearFlights();
      this.eye?.classList.remove('is-blinking');
      this.controller = null;
    }
  }
  customElements.define('rbw-ambient', RbwAmbient);
}

